<template>
  <div class="pipeline-designer">
    <div class="toolbar">
      <h2>Pipeline Creator</h2>
      <div class="actions">
        <button @click="savePipeline" class="btn-primary">Salvar Pipeline</button>
        <button @click="executePipeline" class="btn-success">Executar Pipeline</button>
      </div>
    </div>
    
    <div class="designer-container">
      <div class="modules-panel">
        <h3>Módulos Disponíveis</h3>
        <div 
          v-for="moduleType in availableModules" 
          :key="moduleType.type"
          class="module-item"
          draggable="true"
          @dragstart="onDragStart($event, moduleType)"
        >
          <div class="module-icon" :class="moduleType.type">
            <i :class="moduleType.icon"></i>
          </div>
          <div class="module-name">{{ moduleType.name }}</div>
        </div>
      </div>
      
      <div 
        class="canvas"
        @drop="onDrop($event)"
        @dragover="onDragOver($event)"
        @click="onCanvasClick($event)"
      >
        <!-- Módulos no canvas -->
        <div 
          v-for="module in pipelineModules" 
          :key="module.id"
          class="module-node"
          :class="module.module.type"
          :style="{ left: module.position_x + 'px', top: module.position_y + 'px' }"
          :data-module-id="module.connection_id"
          @mousedown="startDragModule($event, module)"
        >
          <div class="module-header">
            <span>{{ module.module.name }}</span>
            <button @click="removeModule(module)" class="btn-icon">×</button>
          </div>
          <div class="module-body">
            <div class="module-icon">
              <i :class="getModuleIcon(module.module.type)"></i>
            </div>
          </div>
          <div class="connection-points">
            <div class="connection-point input" @mousedown.stop="startConnection($event, module, 'input')"></div>
            <div class="connection-point output" @mousedown.stop="startConnection($event, module, 'output')"></div>
          </div>
        </div>
        
        <!-- Conexões entre módulos -->
        <svg class="connections-layer">
          <path 
            v-for="connection in connections" 
            :key="connection.id"
            :d="getConnectionPath(connection)"
            :class="['connection-path', connection.execution_type]"
            @click="openConnectionMenu(connection, $event)"
          ></path>
        </svg>
        
        <!-- Conexão sendo criada -->
        <svg class="temp-connection-layer" v-if="tempConnection.active">
          <path 
            :d="getTempConnectionPath()"
            class="temp-connection-path"
          ></path>
        </svg>
      </div>
      
      <div class="properties-panel" v-if="selectedModule">
        <h3>Propriedades do Módulo</h3>
        <div class="form-group">
          <label>Nome</label>
          <input type="text" v-model="selectedModule.module.name" />
        </div>
        <div class="form-group">
          <label>Tipo</label>
          <input type="text" :value="getModuleTypeName(selectedModule.module.type)" disabled />
        </div>
        <!-- Propriedades específicas do tipo de módulo -->
        <div class="module-specific-properties">
          <component 
            :is="getModulePropertiesComponent(selectedModule.module.type)"
            :module="selectedModule"
            @update:config="updateModuleConfig"
          ></component>
        </div>
        <div class="form-actions">
          <button @click="closePropertiesPanel" class="btn-secondary">Fechar</button>
          <button @click="applyModuleChanges" class="btn-primary">Aplicar</button>
        </div>
      </div>
    </div>
    
    <!-- Menu de contexto para conexões -->
    <div 
      v-if="connectionMenu.visible" 
      class="connection-context-menu"
      :style="{ left: connectionMenu.position.x + 'px', top: connectionMenu.position.y + 'px' }"
    >
      <h4>Tipo de Execução</h4>
      <div class="menu-options">
        <div 
          class="menu-option" 
          :class="{ active: connectionMenu.connection.execution_type === 'sequential' }"
          @click="setConnectionExecutionType('sequential')"
        >
          <i class="fas fa-arrow-right"></i> Sequencial
        </div>
        <div 
          class="menu-option"
          :class="{ active: connectionMenu.connection.execution_type === 'parallel' }"
          @click="setConnectionExecutionType('parallel')"
        >
          <i class="fas fa-random"></i> Paralelo
        </div>
      </div>
      <div class="menu-actions">
        <button @click="deleteConnection" class="btn-danger">Excluir</button>
        <button @click="closeConnectionMenu" class="btn-secondary">Fechar</button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { v4 as uuidv4 } from 'uuid';
import { pipelineService } from '@/services/services';

// Componentes de propriedades específicas para cada tipo de módulo
import UploadModuleProperties from '@/components/modules/UploadModuleProperties.vue';
import TransformModuleProperties from '@/components/modules/TransformModuleProperties.vue';
import DataQualityModuleProperties from '@/components/modules/DataQualityModuleProperties.vue';
import DbConnectionModuleProperties from '@/components/modules/DbConnectionModuleProperties.vue';
import FilterModuleProperties from '@/components/modules/FilterModuleProperties.vue';
import JoinModuleProperties from '@/components/modules/JoinModuleProperties.vue';
import ExportModuleProperties from '@/components/modules/ExportModuleProperties.vue';

export default {
  name: 'PipelineDesigner',
  
  setup() {
    const router = useRouter();
    const store = useStore();
    
    // Estado do designer
    const pipelineId = ref(null);
    const pipelineName = ref('Novo Pipeline');
    const pipelineModules = ref([]);
    const connections = ref([]);
    const selectedModule = ref(null);
    
    // Estado da conexão temporária (durante a criação)
    const tempConnection = reactive({
      active: false,
      sourceModule: null,
      sourcePoint: null,
      targetPosition: { x: 0, y: 0 }
    });
    
    // Estado do menu de contexto para conexões
    const connectionMenu = reactive({
      visible: false,
      connection: null,
      position: { x: 0, y: 0 }
    });
    
    // Módulos disponíveis para arrastar
    const availableModules = [
      { type: 'uploadData', name: 'Upload de Dados', icon: 'fas fa-upload', id: 1 },
      { type: 'transform', name: 'Transformação', icon: 'fas fa-cogs', id: 2 },
      { type: 'dataQuality', name: 'Data Quality', icon: 'fas fa-check-circle', id: 3 },
      { type: 'dbConnection', name: 'Conexão DB', icon: 'fas fa-database', id: 4 },
      { type: 'filter', name: 'Filtro', icon: 'fas fa-filter', id: 5 },
      { type: 'join', name: 'Join', icon: 'fas fa-link', id: 6 },
      { type: 'export', name: 'Exportação', icon: 'fas fa-file-export', id: 7 },
      { type: 'notification', name: 'Notification', icon: 'fas fa-file-export', id: 8 }
    ];
    
    // Mapeamento de tipos de módulo para componentes de propriedades
    const modulePropertiesComponents = {
      uploadData: UploadModuleProperties,
      transform: TransformModuleProperties,
      dataQuality: DataQualityModuleProperties,
      dbConnection: DbConnectionModuleProperties,
      filter: FilterModuleProperties,
      join: JoinModuleProperties,
      export: ExportModuleProperties
    };
    
    // Carregar pipeline existente ou criar um novo
    onMounted(async () => {
      const id = router.currentRoute.value.params.id;
      if (id && id !== 'new') {
        pipelineId.value = id;
        try {
          const pipeline = await pipelineService.getById(id);
        
          pipelineName.value = pipeline.data.name;
          pipelineModules.value = pipeline.data.pipeline_modules;
          connections.value = pipeline.data.connections.map(conn => ({
            ...conn,
            execution_type: conn.execution_type || 'sequential' // Garantir valor padrão
          }));
        } catch (error) {
          console.error('Erro ao carregar pipeline:', error);
          store.dispatch('notifications/addNotification', {
            type: 'error',
            message: 'Erro ao carregar pipeline'
          });
        }
      }
      
      // Adicionar listener para fechar menus ao clicar fora
      document.addEventListener('click', handleOutsideClick);
    });
    
    onUnmounted(() => {
      document.removeEventListener('click', handleOutsideClick);
    });
    
    // Fechar menus ao clicar fora deles
    const handleOutsideClick = (event) => {
      // Fechar menu de conexão se clicou fora
      if (connectionMenu.visible) {
        const menu = document.querySelector('.connection-context-menu');
        if (menu && !menu.contains(event.target)) {
          closeConnectionMenu();
        }
      }
    };
    
    // Métodos para manipulação de módulos
    
    // Iniciar arrasto de um tipo de módulo
    const onDragStart = (event, moduleType) => {
      event.dataTransfer.setData('moduleType', JSON.stringify(moduleType));
    };
    
    // Permitir soltar no canvas
    const onDragOver = (event) => {
      event.preventDefault();
    };
    
    // Soltar um módulo no canvas
    const onDrop = (event) => {
      event.preventDefault();
      const moduleTypeData = event.dataTransfer.getData('moduleType');
      

      if (moduleTypeData) {
        const moduleType = JSON.parse(moduleTypeData);
        const rect = event.currentTarget.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        
        addModule(moduleType, { x, y });
      }
    };
    
    // Adicionar um novo módulo ao canvas
    const addModule = (moduleType, position) => {
      //console.log('Module Type:', moduleType);
      // const newModule = {
      //   id: Date.now(), // ID temporário, será substituído pelo backend
      //   name: moduleType.name,
      //   type: moduleType.type,
      //   position: position,
      //   config: {} // Configuração vazia por padrão
      // };
      const newModule = {
        id: Date.now(),
        connection_id: Date.now(), // Gerar um ID único para o módulo
        module_id: moduleType.id,
        pipeline_id: pipelineId.value, // se tiver um pipeline selecionado
        position_x: position.x,
        position_y: position.y,
        config: {},
        module: {
          name: moduleType.name,
          type: moduleType.type,
          description: moduleType.description || '',
          config_schema: moduleType.config_schema || {},
          is_active: true,
          id: moduleType.id,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        },
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      // console.log('New Module:', newModule);
      // console.log(pipelineModules.value);
      pipelineModules.value.push(newModule);
      selectedModule.value = newModule; // Selecionar o novo módulo
    };
    
    // Remover um módulo do canvas
    const removeModule = (module) => {
      // Remover conexões associadas a este módulo
      connections.value = connections.value.filter(conn => 
        conn.source_module_id !== module.connection_id && conn.target_module_id !== module.connection_id
      );
      
      // Remover o módulo
      pipelineModules.value = pipelineModules.value.filter(m => m.connection_id !== module.connection_id);
      
      // Limpar seleção se necessário
      if (selectedModule.value && selectedModule.value.connection_id === module.connection_id) {
        selectedModule.value = null;
      }
    };
    
    // Iniciar arrasto de um módulo existente
    const startDragModule = (event, module) => {
      event.preventDefault();
      
      // Selecionar o módulo
      selectedModule.value = module;
      
      
      const initialX = event.clientX;
      const initialY = event.clientY;
      const initialModuleX = module.position_x;
      const initialModuleY = module.position_y;

      
      const onMouseMove = (moveEvent) => {
        const deltaX = moveEvent.clientX - initialX;
        const deltaY = moveEvent.clientY - initialY;
        
        // Atualizar posição do módulo
        module.position_x = initialModuleX + deltaX;
        module.position_y = initialModuleY + deltaY;
      };
      
      const onMouseUp = () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };
      
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    };
    
    // Métodos para manipulação de conexões
    
    // Iniciar criação de uma conexão
    const startConnection = (event, module, pointType) => {
      event.preventDefault();
      
      // Só permitir conexões a partir de pontos de saída
      if (pointType !== 'output') return;
      
      const rect = event.currentTarget.closest('.canvas').getBoundingClientRect();
      const initialX = event.clientX - rect.left;
      const initialY = event.clientY - rect.top;

      tempConnection.active = true;
      tempConnection.sourceModule = module;
      tempConnection.sourcePoint = { x: initialX, y: initialY };
      tempConnection.targetPosition = { x: initialX, y: initialY };

      
      const onMouseMove = (moveEvent) => {
        tempConnection.targetPosition.x = moveEvent.clientX - rect.left;
        tempConnection.targetPosition.y = moveEvent.clientY - rect.top;
       
      };
      
      const onMouseUp = (upEvent) => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
        
        // Verificar se soltou sobre um ponto de conexão de entrada
        const targetElement = document.elementFromPoint(upEvent.clientX, upEvent.clientY);
        
        if (targetElement && targetElement.classList.contains('connection-point') && 
            targetElement.classList.contains('input')) {
            
          // Encontrar o módulo alvo
          const targetModuleElement = targetElement.closest('.module-node');
          
          if (targetModuleElement) {
            const targetModuleId = parseInt(targetModuleElement.getAttribute('data-module-id'));
            
            const targetModule = pipelineModules.value.find(m => m.connection_id === targetModuleId);
            //console.log('AQUI:', targetModule);
            if (targetModule && targetModule.connection_id !== tempConnection.sourceModule.connection_id) {
              // Criar nova conexão
              // console.log('AQUI:', targetModule.connection_id, tempConnection.sourceModule.connection_id);
              createConnection(tempConnection.sourceModule, targetModule);
            }
          }
        }
        
        // Limpar conexão temporária
        tempConnection.active = false;
      };
      
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    };
    
    // Criar uma conexão entre dois módulos
    const createConnection = (sourceModule, targetModule) => {
      
      // Verificar se já existe uma conexão entre estes módulos
      const existingConnection = connections.value.find(conn => 
        conn.source_module_id === sourceModule.connection_id && conn.target_module_id === targetModule.connection_id
      );
      // console.log(existingConnection);

      if (!existingConnection) {
        const newConnection = {
          id: Date.now(), // ID temporário, será substituído pelo backend
          source_module_id: sourceModule.connection_id,
          target_module_id: targetModule.connection_id,
          execution_type: 'sequential', // Tipo padrão: sequencial
          config: {}
        };
        
        connections.value.push(newConnection);
      }
    };
    
    // Abrir menu de contexto para uma conexão
    const openConnectionMenu = (connection, event) => {
      event.stopPropagation();
      
      connectionMenu.visible = true;
      connectionMenu.connection = connection;
      connectionMenu.position = {
        x: event.clientX,
        y: event.clientY
      };
    };
    
    // Fechar menu de contexto de conexão
    const closeConnectionMenu = () => {
      connectionMenu.visible = false;
      connectionMenu.connection = null;
    };
    
    // Definir tipo de execução para uma conexão
    const setConnectionExecutionType = (type) => {
      if (connectionMenu.connection) {
        connectionMenu.connection.execution_type = type;
      }
    };
    
    // Excluir uma conexão
    const deleteConnection = () => {
      if (connectionMenu.connection) {
        connections.value = connections.value.filter(conn => 
          conn.id !== connectionMenu.connection.id
        );
        closeConnectionMenu();
      }
    };
    
    // Obter caminho SVG para uma conexão
    const getConnectionPath = (connection) => {
    
      const sourceModule = pipelineModules.value.find(m => m.connection_id === connection.source_module_id);
      const targetModule = pipelineModules.value.find(m => m.connection_id === connection.target_module_id);

      // console.log('ListPipeline:', connection.source_module_id);
      // console.log('Source Module:', sourceModule);
      
      if (!sourceModule || !targetModule) return '';
      
      const sourceX = sourceModule.position_x + 150; // Ponto de saída à direita
      const sourceY = sourceModule.position_y + 50;  // Centro vertical
      const targetX = targetModule.position_x;       // Ponto de entrada à esquerda
      const targetY = targetModule.position_y + 50;  // Centro vertical
      
      // Calcular pontos de controle para curva
      const controlPointOffset = 80;
      const sourceControlX = sourceX + controlPointOffset;
      const targetControlX = targetX - controlPointOffset;
      // console.log('Source Module:', `M ${sourceX} ${sourceY} C ${sourceControlX} ${sourceY}, ${targetControlX} ${targetY}, ${targetX} ${targetY}`);
      return `M ${sourceX} ${sourceY} C ${sourceControlX} ${sourceY}, ${targetControlX} ${targetY}, ${targetX} ${targetY}`;
    };
    
    // Obter caminho SVG para a conexão temporária
    const getTempConnectionPath = () => {
      if (!tempConnection.active) return '';
      
      const sourceX = tempConnection.sourcePoint.x;
      const sourceY = tempConnection.sourcePoint.y;
      const targetX = tempConnection.targetPosition.x;
      const targetY = tempConnection.targetPosition.y;
      
      // Calcular pontos de controle para curva
      const controlPointOffset = 80;
      const sourceControlX = sourceX + controlPointOffset;
      const targetControlX = targetX - controlPointOffset;
      
      return `M ${sourceX} ${sourceY} C ${sourceControlX} ${sourceY}, ${targetControlX} ${targetY}, ${targetX} ${targetY}`;
    };
    
    // Obter ícone para um tipo de módulo
    const getModuleIcon = (moduleType) => {
      const module = availableModules.find(m => m.type === moduleType);
      return module ? module.icon : 'fas fa-cube';
    };
    
    // Obter nome para um tipo de módulo
    const getModuleTypeName = (moduleType) => {
      
      const module = availableModules.find(m => m.type === moduleType);
      return module ? module.name : moduleType;
    };
    
    // Obter componente de propriedades para um tipo de módulo
    const getModulePropertiesComponent = (moduleType) => {
      return modulePropertiesComponents[moduleType] || null;
    };
    
    // Atualizar configuração de um módulo
    const updateModuleConfig = (config) => {
      
      if (selectedModule.value) {
        selectedModule.value.config = config;
        // console.log(selectedModule.value.config);
      }
    };
    
    // Aplicar alterações ao módulo selecionado
    const applyModuleChanges = () => {
      // Nada a fazer aqui, as alterações já são aplicadas via v-model
      // Mas poderia ser usado para validação ou outras operações
    };
    
    // Fechar painel de propriedades
    const closePropertiesPanel = () => {
      selectedModule.value = null;
    };
    
    // Clicar no canvas (para desselecionar)
    const onCanvasClick = (event) => {
      // Verificar se clicou diretamente no canvas (não em um módulo)
      if (event.target.classList.contains('canvas')) {
        selectedModule.value = null;
      }
    };
    
    // Salvar pipeline
    const savePipeline = async () => {
      try {
        const pipelineData = {
          id: pipelineId.value,
          name: pipelineName.value,
          modules: pipelineModules.value,
          connections: connections.value
        };
        // console.log('Pipeline Data:', pipelineData);
        let result;
        if (pipelineId.value) {
          // Atualizar pipeline existente
          result = await pipelineService.update(pipelineId.value, pipelineData);
        } else {
          // Criar novo pipeline
          result = await pipelineService.create(pipelineData);
          pipelineId.value = result.id;
          router.replace(`/pipelines/${result.id}`);
        }
        
        store.dispatch('notifications/addNotification', {
          type: 'success',
          message: 'Pipeline salvo com sucesso'
        });
      } catch (error) {
        console.error('Erro ao salvar pipeline:', error);
        store.dispatch('notifications/addNotification', {
          type: 'error',
          message: 'Erro ao salvar pipeline'
        });
      }
    };
    
    // Executar pipeline
    const executePipeline = async () => {
      if (!pipelineId.value) {
        // Salvar primeiro se for um novo pipeline
        await savePipeline();
      }
      
      try {
        const result = await pipelineService.executePipeline(pipelineId.value);
        
        store.dispatch('notifications/addNotification', {
          type: 'success',
          message: 'Pipeline iniciado com sucesso'
        });
        
        // Redirecionar para a página de execução
        router.push(`/executions/${result.execution_id}`);
      } catch (error) {
        console.error('Erro ao executar pipeline:', error);
        store.dispatch('notifications/addNotification', {
          type: 'error',
          message: 'Erro ao executar pipeline'
        });
      }
    };
    
    return {
      pipelineId,
      pipelineName,
      pipelineModules,
      connections,
      selectedModule,
      tempConnection,
      connectionMenu,
      availableModules,
      
      onDragStart,
      onDragOver,
      onDrop,
      addModule,
      removeModule,
      startDragModule,
      startConnection,
      createConnection,
      openConnectionMenu,
      closeConnectionMenu,
      setConnectionExecutionType,
      deleteConnection,
      getConnectionPath,
      getTempConnectionPath,
      getModuleIcon,
      getModuleTypeName,
      getModulePropertiesComponent,
      updateModuleConfig,
      applyModuleChanges,
      closePropertiesPanel,
      onCanvasClick,
      savePipeline,
      executePipeline
    };
  }
};
</script>

<style scoped>
.pipeline-designer {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 20px;
  background-color: #f5f5f5;
  border-bottom: 1px solid #ddd;
}

.actions {
  display: flex;
  gap: 10px;
}

.designer-container {
  display: flex;
  flex: 1;
  overflow: hidden;
}

.modules-panel {
  width: 250px;
  padding: 15px;
  background-color: #f9f9f9;
  border-right: 1px solid #ddd;
  overflow-y: auto;
}

.module-item {
  display: flex;
  align-items: center;
  padding: 10px;
  margin-bottom: 10px;
  background-color: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: grab;
  transition: all 0.2s;
}

.module-item:hover {
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
}

.module-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f0f0f0;
  border-radius: 4px;
  margin-right: 10px;
}

.module-icon i {
  font-size: 20px;
  color: #555;
}

.module-name {
  font-size: 14px;
  font-weight: 500;
}

.canvas {
  flex: 1;
  position: relative;
  background-color: #fff;
  background-image: 
    linear-gradient(rgba(200,200,200,0.1) 1px, transparent 1px),
    linear-gradient(90deg, rgba(200,200,200,0.1) 1px, transparent 1px);
  background-size: 20px 20px;
  overflow: auto;
  min-height: 600px;
}

.module-node {
  position: absolute;
  width: 150px;
  background-color: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
  z-index: 10;
}

.module-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 10px;
  background-color: #f5f5f5;
  border-bottom: 1px solid #ddd;
  border-radius: 4px 4px 0 0;
}

.module-body {
  padding: 15px;
  display: flex;
  justify-content: center;
}

.connection-points {
  display: flex;
  justify-content: space-between;
  padding: 0 10px 10px;
}

.connection-point {
  width: 12px;
  height: 12px;
  background-color: #aaa;
  border-radius: 50%;
  cursor: pointer;
}

.connection-point.input {
  background-color: #4CAF50;
}

.connection-point.output {
  background-color: #2196F3;
}

.connection-point:hover {
  transform: scale(1.2);
}

.connections-layer, .temp-connection-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 5;
}

.connection-path {
  fill: none;
  stroke: #2196F3;
  stroke-width: 2px;
  pointer-events: stroke;
  cursor: pointer;
}

.connection-path.sequential {
  stroke: #2196F3;
  stroke-dasharray: none;
}

.connection-path.parallel {
  stroke: #FF9800;
  stroke-dasharray: 5,5;
}

.temp-connection-path {
  fill: none;
  stroke: #aaa;
  stroke-width: 2px;
  stroke-dasharray: 5,5;
}

.properties-panel {
  width: 300px;
  padding: 15px;
  background-color: #f9f9f9;
  border-left: 1px solid #ddd;
  overflow-y: auto;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
}

.form-group input, .form-group select {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

.connection-context-menu {
  position: absolute;
  width: 200px;
  background-color: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  z-index: 100;
  padding: 10px;
}

.connection-context-menu h4 {
  margin-top: 0;
  margin-bottom: 10px;
  padding-bottom: 5px;
  border-bottom: 1px solid #eee;
}

.menu-options {
  margin-bottom: 10px;
}

.menu-option {
  padding: 8px 10px;
  cursor: pointer;
  border-radius: 4px;
}

.menu-option:hover {
  background-color: #f5f5f5;
}

.menu-option.active {
  background-color: #e3f2fd;
  font-weight: 500;
}

.menu-actions {
  display: flex;
  justify-content: space-between;
}

.btn-primary {
  background-color: #2196F3;
  color: white;
  border: none;
  padding: 8px 15px;
  border-radius: 4px;
  cursor: pointer;
}

.btn-success {
  background-color: #4CAF50;
  color: white;
  border: none;
  padding: 8px 15px;
  border-radius: 4px;
  cursor: pointer;
}

.btn-secondary {
  background-color: #f5f5f5;
  color: #333;
  border: 1px solid #ddd;
  padding: 8px 15px;
  border-radius: 4px;
  cursor: pointer;
}

.btn-danger {
  background-color: #f44336;
  color: white;
  border: none;
  padding: 8px 15px;
  border-radius: 4px;
  cursor: pointer;
}

.btn-icon {
  background: none;
  border: none;
  font-size: 16px;
  cursor: pointer;
  color: #666;
}

.btn-icon:hover {
  color: #f44336;
}

/* Estilos específicos para tipos de módulos */
.uploadData .module-icon {
  background-color: #e3f2fd;
}

.transform .module-icon {
  background-color: #e8f5e9;
}

.dataQuality .module-icon {
  background-color: #fff3e0;
}

.dbConnection .module-icon {
  background-color: #e8eaf6;
}

.filter .module-icon {
  background-color: #f3e5f5;
}

.join .module-icon {
  background-color: #e0f7fa;
}

.export .module-icon {
  background-color: #fbe9e7;
}
</style>